import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation configs (extracted to prevent re-renders)
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const fadeInUpDelayed = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay: 0.2 }
};

const fadeInHeader = {
  initial: { opacity: 0, y: -20 },
  animate: { opacity: 1, y: 0 }
};

const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.6, delay: 0.4 }
};

export default function LandingPage({ onLyricsReady }) {
  const [lyrics, setLyrics] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleStartSinging = async () => {
    if (!lyrics.trim()) {
      toast.error("Please paste some lyrics first!");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/lyrics/translate`, {
        lyrics: lyrics.trim(),
        source_language: "Spanish",
        target_language: "English"
      });
      
      onLyricsReady(response.data);
      toast.success("Lyrics translated! Let's learn!");
      navigate("/karaoke");
    } catch (err) {
      toast.error("Failed to translate lyrics. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-default)]">
      {/* Top Banner */}
      <div className="bg-[var(--brand-primary)] text-white py-2 px-4 text-center">
        <a 
          href="https://howtospanish.co/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="font-medium hover:underline"
        >
          🇲🇽 Learn Spanish with HowToSpanish
        </a>
      </div>

      {/* Header */}
      <header className="py-6 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl flex items-center justify-center">
          <motion.div 
            className="flex items-center gap-2"
            {...fadeInHeader}
          >
            <span className="text-3xl">🎤</span>
            <span className="font-bold text-2xl tracking-tight">
              <span className="text-[var(--brand-primary)]">Slang</span>
              <span className="text-[var(--fg-default)]">Decoder</span>
            </span>
          </motion.div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 container mx-auto max-w-4xl px-4 md:px-8 py-8">
        <motion.div
          {...fadeInUp}
          className="text-center mb-10"
        >
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-tight mb-6">
            Ever wondered what your favorite{" "}
            <span className="text-[var(--brand-primary)]">song in Spanish</span>{" "}
            actually means?
          </h1>
          <p className="text-lg md:text-xl text-[var(--fg-muted)] max-w-2xl mx-auto leading-relaxed">
            Paste the lyrics below and get a line-by-line breakdown with translations, slang explanations, and cultural context.
          </p>
        </motion.div>

        {/* Lyrics Input Card */}
        <motion.div
          {...fadeInUpDelayed}
          className="bg-white border-4 border-[var(--fg-default)] shadow-[8px_8px_0px_0px_var(--brand-accent)] rounded-3xl p-6 md:p-8 mb-8"
        >
          {/* User Tip */}
          <div className="bg-[var(--bg-subtle)] border border-gray-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-[var(--fg-muted)]">
              <span className="mr-1">💡</span>
              <strong>Tip:</strong> For more accurate results, paste a full verse or paragraph instead of a single line. The decoder works best when it has enough context to identify the song, artist, slang, and cultural references.
            </p>
          </div>

          <Textarea
            data-testid="lyrics-input"
            value={lyrics}
            onChange={(e) => setLyrics(e.target.value)}
            placeholder="Paste your Spanish lyrics here..."
            className="w-full bg-[var(--bg-subtle)] border-2 border-gray-200 focus:border-[var(--brand-primary)] rounded-2xl p-4 text-lg resize-none transition-colors min-h-[200px] font-lyrics"
          />
          
          <Button
            data-testid="start-singing-btn"
            onClick={handleStartSinging}
            disabled={isLoading || !lyrics.trim()}
            className="w-full mt-6 bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)] rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all active:scale-95 disabled:opacity-50 border-2 border-[var(--fg-default)]"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Translating...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Break It Down
              </>
            )}
          </Button>
        </motion.div>

        {/* Feature Pills */}
        <motion.div
          {...fadeIn}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          <FeaturePill text="Line-by-line translation" color="pink" />
          <FeaturePill text="Slang decoded" color="green" />
          <FeaturePill text="Cultural context" color="pink" />
        </motion.div>

        {/* CTA Section */}
        <motion.div
          {...fadeIn}
          className="bg-white border-4 border-[var(--fg-default)] shadow-[8px_8px_0px_0px_var(--brand-primary)] rounded-3xl p-6 md:p-8 text-center"
        >
          <h3 className="text-xl md:text-2xl font-bold mb-2">
            Love learning Spanish through music?
          </h3>
          <p className="text-[var(--fg-muted)] mb-4">
            Join our live sessions, recorded classes or book a 1:1
          </p>
          <a
            href="https://howtospanish.co/shop"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)] rounded-full px-6 py-3 font-bold transition-all"
          >
            Learn more →
          </a>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 text-center">
        <p className="text-[var(--fg-muted)] text-base">
          <span className="mr-2">🇲🇽</span>
          Made with love by Erika from{" "}
          <a 
            href="https://instagram.com/HowToSpanish" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[var(--brand-primary)] font-semibold hover:underline"
          >
            @HowToSpanish
          </a>
        </p>
      </footer>
    </div>
  );
}

function FeaturePill({ text, color }) {
  const bgColor = color === "pink" 
    ? "bg-[var(--brand-primary)]/10 border-[var(--brand-primary)]" 
    : "bg-[var(--brand-accent)]/30 border-[var(--brand-secondary)]";
  const textColor = color === "pink" 
    ? "text-[var(--brand-primary)]" 
    : "text-[var(--fg-default)]";
  
  return (
    <div className={`${bgColor} ${textColor} border-2 px-4 py-2 rounded-full text-sm font-semibold`}>
      {text}
    </div>
  );
}
